import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowRight, FaPlus, FaTrash, FaSave } from 'react-icons/fa';
import { getChurchServiceById, updateServiceSchedules, ScheduleItem } from '../../services/api';
import EventsManager, { SERVICE_EVENT_TYPES } from './shared/EventsManager';
import AlbumManager from './shared/AlbumManager';

const Container = styled.div`
  padding: 2rem;
  direction: rtl;
  min-height: 80vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #8B0000;
`;

const Title = styled.h1`
  color: #8B0000;
  font-size: 2rem;
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #666;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  transition: background 0.3s;
  &:hover { background: #555; }
`;

const ContentWrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  border-top: 4px solid #8B0000;
  margin-bottom: 2rem;
`;

const SectionTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  color: #8B0000;
  font-size: 1.4rem;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.3s;
  &:hover { background: #45a049; }
  &:disabled { background: #ccc; cursor: not-allowed; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
`;

const TableHeader = styled.th`
  background: #8B0000;
  color: white;
  padding: 0.75rem;
  text-align: right;
  font-size: 1rem;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #eee;
`;

const TableCell = styled.td`
  padding: 0.5rem;
  text-align: right;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  &:focus { outline: none; border-color: #8B0000; }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #8B0000;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  margin: 0 auto;
  &:hover { background: #3f0101; }
`;

const DeleteButton = styled.button`
  padding: 0.5rem;
  background: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: #d32f2f; }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #999;
  font-style: italic;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #666;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 0.5rem 1.5rem;
  border: 2px solid #8B0000;
  border-radius: 20px;
  background: ${p => p.active ? '#8B0000' : 'white'};
  color: ${p => p.active ? 'white' : '#8B0000'};
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s;
  &:hover { background: #8B0000; color: white; }
`;

const AdminServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [serviceName, setServiceName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedules, setSchedules] = useState<ScheduleItem[]>([]);
  const [tab, setTab] = useState<'news' | 'album'>('news');

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    getChurchServiceById(id)
      .then(res => {
        setServiceName(res.data.name);
        setSchedules(res.data.schedules || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const handleSaveSchedules = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await updateServiceSchedules(Number(id), schedules);
      alert('تم الحفظ بنجاح');
    } catch (error) {
      console.error('Error saving:', error);
      alert('حدث خطأ أثناء الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const addRow = () => setSchedules([...schedules, { day: '', time: '', location: '' }]);
  const deleteRow = (index: number) => setSchedules(schedules.filter((_, i) => i !== index));
  const updateRow = (index: number, field: keyof ScheduleItem, value: string) =>
    setSchedules(schedules.map((item, i) => i === index ? { ...item, [field]: value } : item));

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>تفاصيل الخدمة</Title>
          <BackButton to="/admin/services"><FaArrowRight /> العودة</BackButton>
        </Header>
        <LoadingMessage>جاري التحميل...</LoadingMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{serviceName || 'تفاصيل الخدمة'}</Title>
        <BackButton to="/admin/services"><FaArrowRight /> العودة</BackButton>
      </Header>

      <ContentWrapper>
        <SectionCard>
          <SectionTitleRow>
            <SectionTitle>مواعيد الاجتماعات الأسبوعية</SectionTitle>
            <SaveButton onClick={handleSaveSchedules} disabled={saving}>
              <FaSave /> {saving ? 'جاري الحفظ...' : 'حفظ المواعيد'}
            </SaveButton>
          </SectionTitleRow>

          {schedules.length > 0 ? (
            <Table>
              <thead>
                <tr>
                  <TableHeader>اليوم</TableHeader>
                  <TableHeader>الوقت</TableHeader>
                  <TableHeader>المكان</TableHeader>
                  <TableHeader style={{ width: '50px' }}></TableHeader>
                </tr>
              </thead>
              <tbody>
                {schedules.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell><Input value={item.day} onChange={e => updateRow(index, 'day', e.target.value)} placeholder="مثال: الخميس" /></TableCell>
                    <TableCell><Input value={item.time} onChange={e => updateRow(index, 'time', e.target.value)} placeholder="مثال: 7:30 - 9:30 م" /></TableCell>
                    <TableCell><Input value={item.location || ''} onChange={e => updateRow(index, 'location', e.target.value)} placeholder="مثال: قاعة الخدمة" /></TableCell>
                    <TableCell><DeleteButton onClick={() => deleteRow(index)}><FaTrash /></DeleteButton></TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          ) : (
            <EmptyMessage>لا توجد مواعيد بعد</EmptyMessage>
          )}
          <AddButton onClick={addRow}><FaPlus /> إضافة موعد</AddButton>
        </SectionCard>

        <SectionCard>
          <Tabs>
            <Tab active={tab === 'news'} onClick={() => setTab('news')}>الأخبار والفعاليات</Tab>
            <Tab active={tab === 'album'} onClick={() => setTab('album')}>الألبوم والتنزيلات</Tab>
          </Tabs>

          {tab === 'news' && id && (
            <EventsManager serviceId={Number(id)} types={SERVICE_EVENT_TYPES} addLabel="إضافة خبر / فعالية" />
          )}
          {tab === 'album' && id && (
            <AlbumManager serviceId={Number(id)} allowFiles addLabel="إضافة صورة / ملف" />
          )}
        </SectionCard>
      </ContentWrapper>
    </Container>
  );
};

export default AdminServiceDetail;
